<template>
  <div>
    <v-overlay :value="isLoading">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
    <v-container>
      <v-row>
        <v-col>
          <v-btn to="/covid-tracker" color="red" dark>View Data For USA</v-btn>
        </v-col>
      </v-row>
      <v-row align="center">
        <v-col class="d-flex mx-auto" md="3" sm="12">
          <v-select
            color="red"
            :items="arrStates"
            label="State"
            :value="stateData.value"
            @change="onStateChange"
          ></v-select>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <h1 class="text-center">{{ stateData.text }} COVID-19 Tracker</h1>
        </v-col>
      </v-row>
      <v-row>
        <v-col class="text-center">
          <v-btn text small>
            <a class="region-data-link" :href="stateData.site" target="_blank"
              >{{ stateData.text }} Data Source</a
            >
          </v-btn>
        </v-col>
      </v-row>
      <v-row v-if="arrPositive.length > 0 && !isLoading">
        <v-col>
          <v-card class="pa-3">
            <h2 class="text-center">Positive</h2>
            <line-chart
              :chartData="arrPositive"
              :options="chartOptions"
              :chartColors="positiveChartColors"
              label="Positive"
            />
          </v-card>
        </v-col>
      </v-row>
      <v-row v-if="arrHospitalized.length > 0 && !isLoading">
        <v-col>
          <v-card class="pa-3">
            <h2 class="text-center">Hospitalized</h2>
            <line-chart
              :chartData="arrHospitalized"
              :options="chartOptions"
              :chartColors="hospitalizedChartColors"
              label="Hospitalized"
            />
          </v-card>
        </v-col>
      </v-row>
      <v-row v-if="arrInIcu.length > 0 && !isLoading">
        <v-col>
          <v-card class="pa-3">
            <h2 class="text-center">In ICU</h2>
            <line-chart
              :chartData="arrInIcu"
              :options="chartOptions"
              :chartColors="inIcuColors"
              label="In ICU"
            />
          </v-card>
        </v-col>
      </v-row>
      <v-row v-if="arrOnVentilators.length > 0 && !isLoading">
        <v-col>
          <v-card class="pa-3">
            <h2 class="text-center">On Ventilators</h2>
            <line-chart
              :chartData="arrOnVentilators"
              :options="chartOptions"
              :chartColors="onVentilatorsColors"
              label="On Ventilators"
            />
          </v-card>
        </v-col>
      </v-row>
      <v-row v-if="arrRecovered.length > 0 && !isLoading">
        <v-col>
          <v-card class="pa-3">
            <h2 class="text-center">Recovered</h2>
            <line-chart
              :chartData="arrRecovered"
              :options="chartOptions"
              :chartColors="recoveredColors"
              label="Recovered"
            />
          </v-card>
        </v-col>
      </v-row>
      <v-row v-if="arrDeaths.length > 0 && !isLoading">
        <v-col>
          <v-card class="pa-3">
            <h2 class="text-center">Deaths</h2>
            <line-chart
              :chartData="arrDeaths"
              :options="chartOptions"
              :chartColors="deathColors"
              label="Deaths"
            />
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";

import LineChart from "@/components/covid-charts/LineChart";
export default {
  components: {
    LineChart
  },
  data() {
    return {
      arrPositive: [],
      positiveChartColors: {
        borderColor: "#077187",
        pointBorderColor: "#0E1428",
        pointBackgroundColor: "#AFD6AC",
        backgroundColor: "#74A57F"
      },
      arrHospitalized: [],
      hospitalizedChartColors: {
        borderColor: "#251F47",
        pointBorderColor: "#260F26",
        pointBackgroundColor: "#858EAB",
        backgroundColor: "#858EAB"
      },
      arrInIcu: [],
      inIcuColors: {
        borderColor: "#190B28",
        pointBorderColor: "#190B28",
        pointBackgroundColor: "#E55381",
        backgroundColor: "#E55381"
      },
      arrOnVentilators: [],
      onVentilatorsColors: {
        borderColor: "#784F41",
        pointBorderColor: "#784F41",
        pointBackgroundColor: "#BBE5ED",
        backgroundColor: "#BBE5ED"
      },
      arrRecovered: [],
      recoveredColors: {
        borderColor: "#4E5E66",
        pointBorderColor: "#4E5E66",
        pointBackgroundColor: "#31E981",
        backgroundColor: "#31E981"
      },
      arrDeaths: [],
      deathColors: {
        borderColor: "#E06D06",
        pointBorderColor: "#E06D06",
        pointBackgroundColor: "#402A2C",
        backgroundColor: "#402A2C"
      },
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false
      },
      isLoading: false,
      region: "",
      arrStates: [],
      stateData: ""
    };
  },
  async created() {
    this.isLoading = true;
    try {
      this.region = this.$route.params.state;

      const stateRes = await axios.get(
        "https://covidtracking.com/api/states/info"
      );

      stateRes.data.forEach(s => {
        const { state, name, covid19Site } = s;

        this.arrStates.push({ value: state, text: name, site: covid19Site });
      });

      this.stateData = this.arrStates.filter(s => {
        return s.text == this.region;
      })[0];

      await this.getUserStateData(this.stateData.value);
    } catch (error) {
      //do something with error
    }
    this.isLoading = false;
  },
  methods: {
    async getUserStateData(state) {
      const { data } = await axios.get(
        `https://covidtracking.com/api/states/daily`
      );

      let stateData = data.filter(s => {
        return s.state === state;
      });

      stateData.forEach(d => {
        const date = moment(d.date, "YYYYMMDD").format("MM/DD");
        const {
          positive,
          hospitalizedCurrently,
          inIcuCurrently,
          onVentilatorCurrently,
          recovered,
          death
        } = d;

        if (positive) this.arrPositive.push({ date, total: positive });

        if (hospitalizedCurrently)
          this.arrHospitalized.push({ date, total: hospitalizedCurrently });

        if (inIcuCurrently) this.arrInIcu.push({ date, total: inIcuCurrently });

        if (onVentilatorCurrently)
          this.arrOnVentilators.push({ date, total: onVentilatorCurrently });

        if (recovered) this.arrRecovered.push({ date, total: recovered });

        if (death) this.arrDeaths.push({ date, total: death });
      });
      this.isLoading = false;
    },
    async onStateChange(e) {
      this.isLoading = true;
      this.arrPositive = [];
      this.arrHospitalized = [];
      this.arrOnVentilators = [];
      this.arrInIcu = [];
      this.arrRecovered = [];
      this.arrDeaths = [];

      this.stateData = this.arrStates.filter(s => {
        return s.value == e;
      })[0];

      await this.getUserStateData(e);
      this.isLoading = false;
    }
  }
};
</script>

<style scoped>
.region-data-link {
  cursor: pointer;
}
</style>
